import { ColumnType } from 'antd/es/table';
import { SortOrder } from 'antd/lib/table/interface';
import React, { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ProjectId } from 'api/models';
import { Avatar } from 'components/atoms/avatar';
import { useTranslate } from 'hooks/use-translation';
import { fullName } from 'utils/full-name';
import { sortByText } from 'utils/sort-by-text';
import { getProjectDetailUrl } from 'utils/urls';
import { ProjectsReportRecord } from '../types';
import { NameCell, StatusCell, WithEllipsis } from './table-cells';

export type ProjectsReportColumn = ColumnType<ProjectsReportRecord> & {
  titleText?: ReactNode;
  description?: ReactNode;
};

export const projectNameColumn = (
  noOfRows: number,
  translate: ReturnType<typeof useTranslate>,
): ProjectsReportColumn => ({
  titleText: translate('reports.columns.project_name_with_count', {
    count: noOfRows,
  }),
  width: '256px',
  fixed: true,
  key: 'projectName',
  render: (
    _,
    {
      projectId,
      projectName,
      customer: { companyLogoUrl },
    }: ProjectsReportRecord,
  ) => (
    <Link to={getProjectDetailUrl(ProjectId(projectId))} target="_blank">
      <NameCell
        logo={<Avatar name={projectName} src={companyLogoUrl} size={18} />}
        primaryText={projectName}
      />
    </Link>
  ),
  sorter: (
    { projectName: a }: ProjectsReportRecord,
    { projectName: b }: ProjectsReportRecord,
  ) => sortByText(a, b),
  defaultSortOrder: 'ascend' as SortOrder,
  sortDirections: [
    'ascend' as SortOrder,
    'descend' as SortOrder,
    'ascend' as SortOrder,
  ],
});

export const commonColumns = (
  translate: ReturnType<typeof useTranslate>,
): ProjectsReportColumn[] => {
  return [
    {
      titleText: translate('reports.columns.customer_name'),
      key: 'companyName',
      render: (_, { customer: { companyName } }: ProjectsReportRecord) => (
        <WithEllipsis content={companyName} />
      ),
      sorter: (
        { customer: { companyName: a } }: ProjectsReportRecord,
        { customer: { companyName: b } }: ProjectsReportRecord,
      ) => sortByText(a, b),
    },
    {
      titleText: translate('reports.columns.status'),
      key: 'status',
      render: (_, { status }: ProjectsReportRecord) => (
        <StatusCell status={status} />
      ),
      sorter: (
        { status: { label: a } }: ProjectsReportRecord,
        { status: { label: b } }: ProjectsReportRecord,
      ) => sortByText(a, b),
    },
    {
      titleText: translate('reports.columns.project_owner'),
      key: 'projectOwner',
      render: (_, record: ProjectsReportRecord) => (
        <NameCell
          logo={
            <Avatar
              name={fullName(record.projectOwner)}
              src={record.projectOwner.photoUrl}
              size={18}
            />
          }
          primaryText={fullName(record.projectOwner)}
        />
      ),
      sorter: (
        { projectOwner: a }: ProjectsReportRecord,
        { projectOwner: b }: ProjectsReportRecord,
      ) => sortByText(fullName(a), fullName(b)),
    },
    {
      titleText: translate('reports.columns.template'),
      key: 'templateName',
      render: (_, { template }: ProjectsReportRecord) => (
        <WithEllipsis content={template?.templateName ?? '-'} />
      ),
      sorter: (
        { template: a }: ProjectsReportRecord,
        { template: b }: ProjectsReportRecord,
      ) => sortByText(a?.templateName ?? '', b?.templateName ?? ''),
    },
  ];
};
